import { IMainContext, ISendCode, ISuccessfulAuth, IVerificationCodeProps } from '../../../../interfaces';
import React, { useContext, useEffect, useState } from 'react';
import { ArrowBackSvg } from '../../../../icons';
import { useTranslation } from 'react-i18next';
import { sendRecoveryPasswordCode, sendVerificationCode } from '../../../../stores/auth.store';
import { MainContext } from '../../../../context/main.context';
import { DEFAULT_VERIFICATION_CODE_TIME, VERIFICATION_TIME_STYLE } from '../../../../common/constants';
import { SEND_RECOVERY_PASSWORD_CODE_ERROR, SEND_VERIFICATION_CODE_ERROR } from '../../../../common/errorCodes';
import Recaptcha from '../../recaptcha';
import ManualController from '../../../../hoc/ManualController';


export default function VerificationCode({
     type,
     email,
     onClose,
     onResend,
     onChangePassword,
}: IVerificationCodeProps): JSX.Element {
    const { t } = useTranslation();
    const { showCaptcha, modalContent, failedAttemptCenter } = useContext(MainContext);
    const [ code, setCode ] = useState<number>(0);
    const [ time, setTime ] = useState<number>(DEFAULT_VERIFICATION_CODE_TIME);
    const { manualRedirect } = ManualController();
    const [ codeErr, setCodeErr ] = useState<string>('');
    const [ isDisableBtn, setDisableBtn ] = useState<boolean>(true);

    useEffect((): void => _countdownTimer(), []);

    useEffect(() => {
        if (code && !showCaptcha) {
            setDisableBtn(false);
        } else {
            setDisableBtn(true);
        }
    }, [ code, showCaptcha ]);

    const _countdownTimer = (): void => {
        let timer = setInterval((): void => {
            setTime((time: number): number => {
                if (time === 0) {
                    clearInterval(timer);

                    return 0;
                } else {
                    return time - 1;
                }
            });
        }, 1000);
    }

    const _onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        if (String(code).length === 6) {
            if (type === 'registration') {
                _sendVerificationCode();
            } else if (type === 'recoveryPassword') {
                _sendRecoveryPasswordCode();
            }
        } else {
            setCodeErr(t('login.codeNotValid'));
            _failedAttemptCenter();
            return;
        }
    }

    const _failedAttemptCenter = (): void => {
        if (failedAttemptCenter) {
            failedAttemptCenter('add');
        }
    }

    const _sendRecoveryPasswordCode = (): void => {
        sendRecoveryPasswordCode(email, code).then((res: { data: ISendCode }): void => {
            const warningMessage: string | undefined = res?.data?.warningMessage;

            if (warningMessage === 'SEND_RECOVERY_PASSWORD_CODE_INCORRECT') {
                setCodeErr(t('login.codeNotValid'));

                _failedAttemptCenter();
                return;
            }

            const result: boolean|undefined = res?.data?.result;

            if (result && onChangePassword) {
                onChangePassword();
                return;
            }

            manualRedirect(res, 'auth', true, SEND_RECOVERY_PASSWORD_CODE_ERROR);
        }, err => {
            const errData = err?.response?.data || err;

            manualRedirect(errData, 'auth', true, SEND_RECOVERY_PASSWORD_CODE_ERROR);
        });
    }

    const _sendVerificationCode = (): void => {
        sendVerificationCode(email, code).then((res: { data: ISuccessfulAuth }): void => {
            const warningMessage: string | undefined = res?.data?.warningMessage;

            if (warningMessage === 'SEND_VERIFICATION_CODE_INCORRECT') {
                setCodeErr(t('login.codeNotValid'));

                _failedAttemptCenter();
                return;
            }

            const result: string|undefined = res?.data?.result;

            if (result) {
                manualRedirect(result, 'auth');
                return;
            }

            manualRedirect(res, 'auth', true, SEND_VERIFICATION_CODE_ERROR);
        }, err => {
            const errData = err?.response?.data || err;

            manualRedirect(errData, 'auth', true, SEND_VERIFICATION_CODE_ERROR);
        });
    }

    const _setCode = (value: string): void => {
        setCode(Number(value.replace(/[^0-9]/g, '')));
    }
    
    const _resendCode = (): void => {
        if (time) {
            return;
        }

        setCode(0);
        setTime(DEFAULT_VERIFICATION_CODE_TIME);
        onResend();
        _countdownTimer();
    }

    return (
        <div className="auth-modal-menu">
            <div className="login-modal-header">
                <div className="login-modal-header-prev" onClick={() => onClose()}>
                    <ArrowBackSvg/>
                    {t('common.back')}
                </div>
                <div className="login-modal-header-title">
                    {t('login.confirmationCode')}
                </div>
                <div className="verification-code-email">
                    {email}
                </div>
            </div>
            <form
                noValidate
                className="auth-modal-menu-body verification-code-auth-modal-menu-body"
                onSubmit={(e) => _onSubmit(e)}
            >
                <div className="login-modal-menu-body-input">
                    <div className="login-modal-menu-body-input-title">
                        {t('login.code')}
                    </div>
                    <input
                        className="login-modal-menu-body-input-item"
                        type="text"
                        required
                        autoComplete="off"
                        autoFocus
                        value={code || ''}
                        onChange={(e) => _setCode(e.target.value)}
                        style={{border: `1px solid ${codeErr ? '#B01E1E' : '#d1d1d0'}`}}
                    >
                    </input>
                    { codeErr ? <div className="req-input-err">{codeErr}</div> : null }
                </div>
                <button
                    className="btn"
                    type="submit"
                    disabled={isDisableBtn}
                    style={{
                        background: isDisableBtn ? '#D1D1D0' : '#2368a2',
                        cursor: isDisableBtn ? 'auto' : 'pointer',
                    }}
                >
                    {t('login.confirmation')}
                </button>
                <div className="auth-modal-menu-footer req-auth-modal-menu-footer">
                    <div
                        className="verification-code-footer-btn text-overflow"
                        style={!time ? VERIFICATION_TIME_STYLE : {}}
                        onClick={() => _resendCode()}
                    >
                        {t(`login.${!time ? 'resend' : 'codeIsValid'}`)}
                    </div>
                    {
                        time ?
                            <div className="auth-modal-menu-footer-reg text-overflow">
                                {`${Math.floor(time / 60)}`.padStart(2, '0')}:{`${time % 60}`.padStart(2, '0')}
                            </div>
                        : null
                    }
                </div>
            </form>
            <Recaptcha/>
        </div>
    );
}